import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { HttpError, request } from "../../../api/http";
import {
  IconCard,
  IconCheck,
  IconClipboardList,
  IconCopy,
  IconFolder,
} from "../../../components/Icons";
import { useConfirm } from "../../../components/Dialog";
import { useAuth } from "../../../context/AuthContext";
import { LeadStatus } from "../../../types/onboarding";
import { LeadDetail } from "../../../types/leadDetail";
import CancellationDrawer from "../../OnboardingCustomers/components/CancellationDrawer";
import CancellationRequestDrawer from "../../OnboardingCustomers/components/CancellationRequestDrawer";
import ChooseCancellationTypeModal from "../../OnboardingCustomers/components/ChooseCancellationTypeModal";
import FileExplorer from "../../OnboardingCustomers/components/FileExplorer";
import StatusChangeButton from "../../OnboardingCustomers/components/StatusChangeButton";
import AnswersExplorer from "./AnswersExplorer";
import DocumentDownloadButton from "./DocumentDownloadButton";
import ManualTarificationDrawer from "./ManualTarificationDrawer";

const isTipo1Eligible = (status: string): boolean =>
  status === "complete" || status === "insurer_review";

const STRIPE_DASHBOARD = "https://dashboard.stripe.com/customers/";

type Props = {
  detail: LeadDetail;
  onStatusChange: (s: LeadStatus) => void;
  onReload: () => void;
};

export default function QuickActionsBar({
  detail,
  onStatusChange,
  onReload,
}: Props) {
  const { token } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [filesOpen, setFilesOpen] = useState(false);
  const [answersOpen, setAnswersOpen] = useState(false);
  const [disablingEmail, setDisablingEmail] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [cancellationChooserOpen, setCancellationChooserOpen] = useState(false);
  const [cancellationDrawerOpen, setCancellationDrawerOpen] = useState(false);
  const [cancellationRequestDrawerOpen, setCancellationRequestDrawerOpen] =
    useState(false);
  const [manualTarificationOpen, setManualTarificationOpen] = useState(false);

  const launchCancellation = () => {
    // When the lead is already canceled, skip the type chooser — there is
    // nothing to choose, just open the same drawer to edit the existing
    // cancellation in place.
    if (detail.status === "canceled") {
      setCancellationDrawerOpen(true);
      return;
    }
    if (isTipo1Eligible(detail.status)) {
      setCancellationChooserOpen(true);
    } else {
      setCancellationDrawerOpen(true);
    }
  };

  const stripeCustomerId = detail.payment?.stripeCustomerId ?? null;
  const signatureLink = detail.contract?.signatureLink ?? null;

  const handleCopySignature = async () => {
    if (!signatureLink) return;
    try {
      await navigator.clipboard.writeText(signatureLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  const customerLabel = [detail.name, detail.lastName, detail.lastName2]
    .filter(Boolean)
    .join(" ");

  const emailAlreadyDisabled = (detail.email ?? "").startsWith("NONE_ADMINAPP_");

  const handleDisableEmail = async () => {
    if (disablingEmail || emailAlreadyDisabled) return;
    const ok = await confirm({
      title: "Deshabilitar email",
      message:
        "Esto bloqueará el email del lead para que pueda registrarse de nuevo con la misma dirección. ¿Continuar?",
      confirmLabel: "Deshabilitar email",
      tone: "danger",
    });
    if (!ok) return;
    setDisablingEmail(true);
    setActionError(null);
    try {
      await request<{ disabled: boolean }>(
        `/api/admin/operations/leads/${detail.leadUlid}/disable-email`,
        { method: "POST", token },
      );
      onReload();
    } catch (err) {
      if (err instanceof HttpError) {
        const body = err.body as { message?: string } | undefined;
        setActionError(body?.message ?? `HTTP ${err.status}`);
      } else {
        setActionError("No se pudo deshabilitar");
      }
    } finally {
      setDisablingEmail(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "0.5rem",
        background: "white",
        padding: "0.6rem 0.85rem",
        borderRadius: "0.55rem",
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        marginBottom: "0.75rem",
        alignItems: "center",
      }}
    >
      <span
        style={{
          fontSize: ".7rem",
          fontWeight: 600,
          color: "#73879C",
          textTransform: "uppercase",
          letterSpacing: ".4px",
          marginRight: "0.25rem",
        }}
      >
        Acciones
      </span>

      <ActionLink
        href={stripeCustomerId ? `${STRIPE_DASHBOARD}${stripeCustomerId}` : undefined}
        disabled={!stripeCustomerId}
        title={stripeCustomerId ? `Abrir ${stripeCustomerId} en Stripe` : "Sin Stripe ID"}
        icon={<IconCard style={{ width: ".95rem", height: ".95rem" }} />}
        label="Stripe"
      />
      <ActionButton
        onClick={handleCopySignature}
        disabled={!signatureLink}
        title={
          copied
            ? "¡Copiado!"
            : signatureLink
              ? "Copiar Signature Link"
              : "Sin signature link"
        }
        icon={
          copied ? (
            <IconCheck style={{ width: ".95rem", height: ".95rem" }} />
          ) : (
            <IconCopy style={{ width: ".95rem", height: ".95rem" }} />
          )
        }
        label={copied ? "Copiado" : "Signature link"}
        tone={copied ? "success" : undefined}
      />
      <ActionButton
        onClick={() => setFilesOpen(true)}
        title="Ver archivos del lead"
        icon={<IconFolder style={{ width: ".95rem", height: ".95rem" }} />}
        label="Archivos"
      />
      <ActionButton
        onClick={() => setAnswersOpen(true)}
        title="Tree de respuestas + historial completo (answers map)"
        icon={<IconClipboardList style={{ width: ".95rem", height: ".95rem" }} />}
        label="Answers"
      />
      <DocumentDownloadButton
        leadUlid={detail.leadUlid}
        token={token}
        leadStatus={detail.status}
      />
      <ActionButton
        onClick={() =>
          detail.subscriptionId && navigate(`/subscriptions/${detail.subscriptionId}`)
        }
        disabled={!detail.subscriptionId}
        title={
          detail.subscriptionId
            ? "Ver suscripción + invoices"
            : "Este lead no tiene suscripción"
        }
        icon={<IconCard style={{ width: ".95rem", height: ".95rem" }} />}
        label="Subscription"
      />
      <StatusChangeButton
        leadUlid={detail.leadUlid}
        currentStatus={detail.status as LeadStatus}
        onStatusChange={onStatusChange}
      />
      <ActionButton
        onClick={launchCancellation}
        title={
          detail.status === "canceled"
            ? "Editar motivo / fechas de la cancelación (no se reenvía email)"
            : isTipo1Eligible(detail.status)
              ? "Cancelar póliza (elige inmediata o solicitud del cliente)"
              : "Cancelar lead con motivo y dispara email automático al cliente"
        }
        icon={null}
        label={detail.status === "canceled" ? "Editar cancelación" : "Cancelar"}
        tone="danger"
      />
      {detail.hasTelesubscription && (
        <ActionButton
          onClick={() => setManualTarificationOpen(true)}
          title="Forzar tarificación manual (lead telesuscripción)"
          icon={null}
          label="Manual tarification"
        />
      )}
      <ActionButton
        onClick={handleDisableEmail}
        disabled={disablingEmail || emailAlreadyDisabled}
        title={
          emailAlreadyDisabled
            ? "El email ya está deshabilitado"
            : "Bloquea el email actual prefijando NONE_ADMINAPP_{timestamp}_ y libera la dirección para registro futuro"
        }
        icon={null}
        label={disablingEmail ? "Deshabilitando…" : "Deshabilitar email"}
        tone="danger"
      />

      {actionError && (
        <span
          style={{
            fontSize: ".75rem",
            color: "#b32424",
            marginLeft: "0.4rem",
          }}
        >
          {actionError}
        </span>
      )}

      {filesOpen && (
        <FileExplorer
          leadId={detail.leadId}
          leadUlid={detail.leadUlid}
          customer={customerLabel}
          currentStatus={detail.status as LeadStatus}
          onStatusChange={onStatusChange}
          onClose={() => setFilesOpen(false)}
        />
      )}
      {answersOpen && (
        <AnswersExplorer
          leadUlid={detail.leadUlid}
          onClose={() => setAnswersOpen(false)}
        />
      )}

      {cancellationChooserOpen && (
        <ChooseCancellationTypeModal
          onClose={() => setCancellationChooserOpen(false)}
          onChoose={(type) => {
            setCancellationChooserOpen(false);
            if (type === "immediate") {
              setCancellationDrawerOpen(true);
            } else {
              setCancellationRequestDrawerOpen(true);
            }
          }}
        />
      )}
      {cancellationDrawerOpen && (
        <CancellationDrawer
          leadUlid={detail.leadUlid}
          onClose={() => setCancellationDrawerOpen(false)}
          onApplied={() => {
            setCancellationDrawerOpen(false);
            onStatusChange("canceled");
          }}
        />
      )}
      {cancellationRequestDrawerOpen && (
        <CancellationRequestDrawer
          leadUlid={detail.leadUlid}
          onClose={() => setCancellationRequestDrawerOpen(false)}
          onApplied={() => {
            setCancellationRequestDrawerOpen(false);
            // Tipo 1: lead stays in current status until carrier effective
            // date is recorded; refresh to surface the pending badge.
            onReload();
          }}
        />
      )}
      {manualTarificationOpen && (
        <ManualTarificationDrawer
          leadUlid={detail.leadUlid}
          leadInternalId={detail.leadId}
          defaultCapital={detail.purchase?.capital ?? null}
          defaultCoverage={detail.purchase?.insuranceType ?? null}
          defaultEffectDate={detail.effectDate ?? null}
          onClose={() => setManualTarificationOpen(false)}
          onApplied={() => {
            setManualTarificationOpen(false);
            // Applying a manual tarification re-prices the lead and moves it
            // to payment-method-pending; reload to reflect the new state.
            onReload();
          }}
        />
      )}
    </div>
  );
}

function ActionButton({
  onClick,
  disabled,
  title,
  icon,
  label,
  tone,
}: {
  onClick: () => void;
  disabled?: boolean;
  title: string;
  icon: React.ReactNode;
  label: string;
  tone?: "success" | "danger";
}) {
  const bg = tone === "success" ? "#1a7f37" : tone === "danger" ? "#fde2e2" : "#f8f9fb";
  const fg = tone === "success" ? "white" : tone === "danger" ? "#b32424" : "#1f2933";
  const border =
    tone === "success" ? "#1a7f37" : tone === "danger" ? "#f5c2c2" : "#dee2e6";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.35rem",
        padding: "0.4rem 0.7rem",
        borderRadius: "0.4rem",
        background: bg,
        color: fg,
        border: `1px solid ${border}`,
        fontSize: ".78rem",
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function ActionLink({
  href,
  disabled,
  title,
  icon,
  label,
}: {
  href: string | undefined;
  disabled?: boolean;
  title: string;
  icon: React.ReactNode;
  label: string;
}) {
  if (disabled || !href) {
    return (
      <span
        title={title}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.35rem",
          padding: "0.4rem 0.7rem",
          borderRadius: "0.4rem",
          background: "#f8f9fb",
          color: "#1f2933",
          border: "1px solid #dee2e6",
          fontSize: ".78rem",
          fontWeight: 600,
          opacity: 0.5,
          cursor: "not-allowed",
        }}
      >
        {icon}
        <span>{label}</span>
      </span>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.35rem",
        padding: "0.4rem 0.7rem",
        borderRadius: "0.4rem",
        background: "#f8f9fb",
        color: "#1f2933",
        border: "1px solid #dee2e6",
        fontSize: ".78rem",
        fontWeight: 600,
        textDecoration: "none",
      }}
    >
      {icon}
      <span>{label}</span>
    </a>
  );
}
